import { FoaPart, ProcedureType, Regulation } from "../../autogen/bff-api";
import { isDesignContest, isDpsCompetition, isDpsScheme, isTwoStageProcedure } from "./procedure";

// Client mirror of constants the BFF owns; a drift here misleads the buyer without failing anything,
// since the BFF still rejects a bad deadline. Sources:
// bff/domain/.../sourcingevent/v2/Standstill.kt
// bff/domain/.../sourcingevent/v2/ProcedureRules.kt
// bff/domain/.../sourcingevent/v2/publicprocurement/ProcurementThresholds.kt (the part pair below)
const EEA_TIERS: Record<"FOA" | "UTILITIES", { ordinary: FoaPart; specialServices: FoaPart }> = {
  FOA: { ordinary: FoaPart.PartIii, specialServices: FoaPart.PartIv },
  UTILITIES: { ordinary: FoaPart.PartIi, specialServices: FoaPart.PartIii },
};

const tiersFor = (regulation: Regulation | null | undefined) =>
  regulation === Regulation.Forsyningsforskriften ? EEA_TIERS.UTILITIES : EEA_TIERS.FOA;

export const isAtOrAboveEea = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => {
  const tiers = tiersFor(regulation);
  return foaPart === tiers.ordinary || foaPart === tiers.specialServices;
};

export const isFoaNationalTier = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => (regulation ?? Regulation.Foa) === Regulation.Foa && foaPart === FoaPart.PartIi;

export const isOrdinaryEeaTier = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
): boolean => foaPart === tiersFor(regulation).ordinary;

export const autoKarensDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
): number | null => {
  if (isDpsScheme(procedureType) || isDpsCompetition(procedureType) || isDesignContest(procedureType)) return null;
  if (procedureType === "NEGOTIATED_WITHOUT_PRIOR_CALL") return null;
  return isAtOrAboveEea(regulation, foaPart) ? 10 : null;
};

// FOA §§ 20-2 to 20-4 (30 or 25 days) and § 26-7 (10); forsyningsforskriften § 16-2 (30) and § 16-3 (10).
// FOA's del IV is over the threshold but on the light regime, so only del III carries fixed numbers.
export const minTenderDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
  accelerated: boolean = false,
): number | null => {
  if (!isOrdinaryEeaTier(regulation, foaPart)) return null;
  const urgent = accelerated ? acceleratedDays(regulation, foaPart, procedureType) : null;
  if (urgent) return urgent.tender;
  if (isDpsScheme(procedureType)) return null;
  if (isDpsCompetition(procedureType)) return 10;
  // § 9-2 and § 13-3 publish no notice, so no minimum runs from one.
  if (procedureType === "NEGOTIATED_WITHOUT_PRIOR_CALL") return null;
  if (isDesignContest(procedureType)) return null;
  if (procedureType == null) {
    return regulation === Regulation.Forsyningsforskriften ? null : 30;
  }
  if (regulation === Regulation.Forsyningsforskriften) {
    return isTwoStageProcedure(procedureType) ? 10 : 30;
  }
  return isTwoStageProcedure(procedureType) ? 25 : 30;
};

// An interpolation value, never a translation key: a paragraph reference reads the same in both
// languages, and translating one would invent a citation that does not exist.
export const tenderDeadlineProvision = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType?: ProcedureType | null,
): string | null => {
  if (minTenderDays(regulation, foaPart, procedureType) == null) return null;
  if (isDpsCompetition(procedureType)) {
    return regulation === Regulation.Forsyningsforskriften ? "forsyningsforskriften § 22-5 (3)" : "FOA § 26-7 (3)";
  }
  if (regulation !== Regulation.Forsyningsforskriften) return foaTenderDeadlineSection(procedureType);
  return isTwoStageProcedure(procedureType) ? "forsyningsforskriften § 16-3" : "forsyningsforskriften § 16-2";
};

// FOA §§ 20-3 and 20-4: 30 days from the notice. Forsyningsforskriften § 16-3 (1): 30 as the general rule,
// never under 15; its dynamic purchasing system keeps 30.
export const minRequestDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
  accelerated: boolean = false,
): number | null => {
  if (!isOrdinaryEeaTier(regulation, foaPart) || !isTwoStageProcedure(procedureType) || isDesignContest(procedureType)) {
    return null;
  }
  const urgent = accelerated ? acceleratedDays(regulation, foaPart, procedureType) : null;
  if (urgent?.request) return urgent.request;
  return requestDaysGeneralRule(regulation, foaPart, procedureType) != null ? 15 : 30;
};

export const requestDaysGeneralRule = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): number | null =>
  regulation === Regulation.Forsyningsforskriften &&
  isOrdinaryEeaTier(regulation, foaPart) &&
  isTwoStageProcedure(procedureType) &&
  !isDesignContest(procedureType) &&
  procedureType !== ProcedureType.DynamicPurchasingSystem &&
  procedureType !== ProcedureType.QualificationScheme
    ? 30
    : null;

// FOA § 16-12 (3): 5 for a restricted tender, 3 for the negotiated family; FOA § 8-9 (3): 3 in Del II.
// Forsyningsforskriften has no verified counterpart, so none is enforced there.
export const minCandidatesWhenCapped = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): number | null => {
  if (regulation === Regulation.Forsyningsforskriften) return null;
  if (procedureType === ProcedureType.RestrictedTenderCompetition && isFoaNationalTier(regulation, foaPart)) return 3;
  if (!isOrdinaryEeaTier(regulation, foaPart) || !isTwoStageProcedure(procedureType)) return null;
  return procedureType === ProcedureType.Restricted || procedureType === ProcedureType.RestrictedTenderCompetition
    ? 5
    : 3;
};

// Forsyningsforskriften § 17-2 (2) c and FOA § 21-2 (2) c; mirrors PriorInformationCall.
export const PRIOR_INFORMATION_CALL = {
  earliestInvitationDays: 35,
  latestInvitationMonths: 12,
} as const;

export type InterestRequestFloor = { minDays: number; generalRuleDays: number | null; provision: string };

// Forsyningsforskriften § 16-3 (1) allows down to 15 days with a reason; FOA § 20-3 (1) and § 20-4 (1) keep 30,
// and FOA § 20-3 (5) a shortens it to 15 when the procurement is urgent. Mirrors PriorInformationCall.
export const interestRequestFloor = (regulation: Regulation | null | undefined, urgent = false): InterestRequestFloor => {
  if (regulation !== Regulation.Foa) {
    return { minDays: 15, generalRuleDays: 30, provision: "forsyningsforskriften § 16-3 (1)" };
  }
  return urgent
    ? { minDays: 15, generalRuleDays: null, provision: "FOA § 20-3 (5) a" }
    : { minDays: 30, generalRuleDays: null, provision: "FOA § 20-3 (1)" };
};

export const usesAcceleratedProcedure = (ctx: {
  regulation?: Regulation | null;
  foaPart?: FoaPart | null;
  procedureType?: ProcedureType | null;
  acceleratedProcedure?: boolean | null;
}): boolean => ctx.acceleratedProcedure === true && offersAcceleratedProcedure(ctx.regulation, ctx.foaPart, ctx.procedureType);

// Mirrors Instant.plus(days), by which the BFF still judges the FOA minimums: whole 24-hour days, so a
// deadline across the clock change on the last Sunday of October is judged as the server judges it.
// FOA kap. 20: § 20-2 open, § 20-3 restricted, § 20-4 negotiated with prior notice. § 20-5 (competitive
// dialogue, innovation partnership) sets only the request deadline, so it is cited for no tender period.
const FOA_TENDER_DEADLINE_SECTION: Partial<Record<ProcedureType, string>> = {
  [ProcedureType.Open]: "FOA § 20-2",
  [ProcedureType.Restricted]: "FOA § 20-3",
  [ProcedureType.NegotiatedWithPriorCall]: "FOA § 20-4",
};

const foaTenderDeadlineSection = (procedureType: ProcedureType | null | undefined): string | null =>
  procedureType == null ? FOA_TENDER_DEADLINE_SECTION[ProcedureType.Open]! : FOA_TENDER_DEADLINE_SECTION[procedureType] ?? null;

// FOA § 20-2 (3), § 20-3 (5) and § 20-4 (3); forsyningsforskriften § 16-2 (3). Mirrors ProcedureRules.acceleratedFor.
const acceleratedDays = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): { request: number | null; tender: number } | null => {
  if (!isOrdinaryEeaTier(regulation, foaPart)) return null;
  if (regulation === Regulation.Forsyningsforskriften) {
    return procedureType === ProcedureType.Open ? { request: null, tender: 15 } : null;
  }
  if (procedureType === ProcedureType.Open) return { request: null, tender: 15 };
  if (procedureType === ProcedureType.Restricted || procedureType === ProcedureType.NegotiatedWithPriorCall) {
    return { request: 15, tender: 10 };
  }
  return null;
};

export const offersAcceleratedProcedure = (
  regulation: Regulation | null | undefined,
  foaPart: FoaPart | null | undefined,
  procedureType: ProcedureType | null | undefined,
): boolean => acceleratedDays(regulation, foaPart, procedureType) != null;
